import {
  Alert,
  AlertIcon,
  AlertTitle,
  Box,
  HStack,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';

import { DefaultTFuncReturn } from 'i18next';
import { InView } from 'react-intersection-observer';
import Loading from '@/client/components/media/Loading';
import OutlineButton from '@/client/components/shared/buttons/OutlineButton';
import { format } from 'date-fns';
import { useGetUserBadgesInfinite } from '@/client/services/hooks/badges/useGetUserBadgesInfinite';
import { useTranslation } from 'react-i18next';

interface LearnerBadgesListProps {
  userId: string | undefined;
  modalNoDataCopy: DefaultTFuncReturn;
  noDataPlaceholder: React.ReactNode;
}

export default function LearnerBadgesList({
  userId,
  modalNoDataCopy,
  noDataPlaceholder,
}: LearnerBadgesListProps) {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const perPage = isOpen ? 50 : 6;

  const { data, isError, isLoading, fetchNextPage, isFetchingNextPage } = useGetUserBadgesInfinite(
    userId,
    perPage,
  );

  if (isLoading) {
    return <Loading />;
  }

  if (isError) {
    return (
      <Alert status="error" maxWidth="container.sm">
        <AlertIcon />

        <AlertTitle mr={2}>{t('learners.badges.error')}</AlertTitle>
      </Alert>
    );
  }

  return (
    <>
      {data?.pages[0]?.length === 0 && noDataPlaceholder}

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} width="full">
        {data?.pages[0].map((badgeAward) => {
          if (!badgeAward.badge) {
            return null;
          }

          return (
            <HStack
              key={badgeAward._id}
              spacing={4}
              paddingX={6}
              paddingY={4}
              borderRadius="16px"
              border="1px solid"
              borderColor="neutral.100"
            >
              <Image
                height="70px"
                width="70px"
                src={
                  badgeAward.badge.uploaded_image ||
                  '/public/images/Acheivement-1--Streamline-Ux.png'
                }
                alt="Achievement"
              />

              <Stack>
                <Text fontSize="14px" fontWeight={600} color="baseBlack">
                  {badgeAward.badge.title}
                </Text>

                <Text fontSize="12px" fontWeight={500} color="neutral.800">
                  {format(new Date(badgeAward.acquired_date), 'MMMM do, yyyy')}
                </Text>
              </Stack>
            </HStack>
          );
        })}
      </SimpleGrid>

      {data?.pages[0].length === 6 && (
        <OutlineButton width="full" marginTop={4} onClick={onOpen}>
          {t('learners.badges.showAll')}
        </OutlineButton>
      )}

      <Modal isCentered isOpen={isOpen} onClose={onClose} size="4xl">
        <ModalOverlay />

        <ModalContent maxHeight="80vh">
          <ModalHeader>{t('learners.badges')}</ModalHeader>

          <ModalCloseButton />

          <ModalBody overflowY="auto">
            {isLoading ? (
              <Loading />
            ) : isError ? (
              <Alert status="error">
                <AlertIcon />
                <AlertTitle>{t('learners.badges.error')}</AlertTitle>
              </Alert>
            ) : data?.pages[0]?.length === 0 ? (
              <Box padding={10} textAlign="center" fontSize="sm" color="blackAlpha.600">
                {modalNoDataCopy}
              </Box>
            ) : (
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                {data?.pages.flatMap((page) =>
                  page.map((badgeAward) => {
                    if (!badgeAward.badge) {
                      return null;
                    }

                    return (
                      <HStack
                        key={badgeAward._id}
                        spacing={4}
                        paddingX={6}
                        paddingY={4}
                        borderRadius="16px"
                        border="1px solid"
                        borderColor="neutral.100"
                      >
                        <Image
                          height="70px"
                          width="70px"
                          src={
                            badgeAward.badge.uploaded_image ||
                            '/public/images/Acheivement-1--Streamline-Ux.png'
                          }
                          alt="Achievement"
                        />

                        <Stack>
                          <Text fontSize="14px" fontWeight={600} color="baseBlack">
                            {badgeAward.badge.title}
                          </Text>

                          <Text fontSize="12px" fontWeight={500} color="neutral.800">
                            {format(new Date(badgeAward.acquired_date), 'MMMM do, yyyy')}
                          </Text>
                        </Stack>
                      </HStack>
                    );
                  }),
                )}

                {/* Infinite scroll trigger */}
                <InView
                  rootMargin="50px"
                  as="div"
                  threshold={0}
                  onChange={(inView) => {
                    if (inView && !isFetchingNextPage) {
                      fetchNextPage();
                    }
                  }}
                />

                {isFetchingNextPage && <Loading />}
              </SimpleGrid>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
